import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import AppFooter from '../components/AppFooter';
import PasskeyManager from '../components/PasskeyManager';
import { PushReminderToggle, PushReminderPreferences } from '../components/PushReminderCard';
import { toast } from 'sonner';
import {
  Shield, ArrowLeft, Lock, Mail, Bell, Trash2, Download,
  AlertTriangle, Eye, EyeOff, Settings
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();

  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Email
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);

  // Notifications
  const [notifPrefs, setNotifPrefs] = useState({
    welcome_email: true, dispute_updates: true,
    timer_reminders: true, resolve_celebrations: true
  });
  const [savingNotifs, setSavingNotifs] = useState(false);

  // Danger zone
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadNotificationPrefs(); }, []);

  const loadNotificationPrefs = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/notifications`);
      setNotifPrefs(data);
    } catch {}
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setChangingPassword(true);
    try {
      await axios.put(`${API_URL}/api/settings/change-password`, {
        current_password: currentPassword, new_password: newPassword
      });
      toast.success('Password changed successfully');
      setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally { setChangingPassword(false); }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    if (!newEmail || !emailPassword) { toast.error('Email and password required'); return; }
    setChangingEmail(true);
    try {
      await axios.put(`${API_URL}/api/settings/change-email`, {
        email: newEmail, password: emailPassword
      });
      toast.success('Email updated successfully');
      setNewEmail(''); setEmailPassword('');
      checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change email');
    } finally { setChangingEmail(false); }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifs(true);
    try {
      await axios.put(`${API_URL}/api/settings/notifications`, notifPrefs);
      toast.success('Notification preferences saved');
    } catch { toast.error('Failed to save preferences'); }
    finally { setSavingNotifs(false); }
  };

  const handleExportData = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/settings/export-data`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `selah_slate_data_export_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch { toast.error('Export failed'); }
  };

  const handleDeleteUploads = async () => {
    try {
      await axios.post(`${API_URL}/api/settings/delete-uploads`);
      toast.success('All uploaded data deleted');
    } catch { toast.error('Failed to delete uploads'); }
  };

  const handleDeleteDisputes = async () => {
    try {
      await axios.post(`${API_URL}/api/settings/delete-disputes`);
      toast.success('Dispute history deleted');
    } catch { toast.error('Failed to delete disputes'); }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) { toast.error('Password required to delete account'); return; }
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/settings/delete-account`, {
        data: { password: deletePassword }
      });
      toast.success('Account deleted. We wish you well on your journey.');
      await logout();
      navigate('/login');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete account');
    } finally { setDeleting(false); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const PasswordIcon = showPasswords ? EyeOff : Eye;

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col" data-testid="settings-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">Selah Slate&trade;</span>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="text-[#36454F]/70">Sign Out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        <Link to="/dashboard" className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />Back to Dashboard
        </Link>

        <div className="mb-8">
          <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#36454F] flex items-center">
            <Settings className="w-7 h-7 mr-3 text-[#B76E79]" strokeWidth={1.5} />Settings
          </h1>
          <p className="mt-1 text-[#36454F]/65 font-['Manrope']">Manage your account, notifications, and privacy</p>
        </div>

        <div className="space-y-6">
          {/* Change Password */}
          <Card className="border-[#36454F]/15 bg-white" data-testid="password-section">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F] flex items-center">
                <Lock className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />Change Password
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="flex items-center justify-end">
                  <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="text-xs text-[#36454F]/50 font-['Manrope'] flex items-center hover:text-[#B76E79]">
                    <PasswordIcon className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />{showPasswords ? 'Hide' : 'Show'}
                  </button>
                </div>
                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">Current Password</Label>
                  <Input type={showPasswords ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="mt-1 h-10 border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope']" required data-testid="current-password" />
                </div>
                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">New Password</Label>
                  <Input type={showPasswords ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="mt-1 h-10 border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope']" required data-testid="new-password" />
                </div>
                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">Confirm New Password</Label>
                  <Input type={showPasswords ? 'text' : 'password'} value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} className="mt-1 h-10 border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope']" required data-testid="confirm-new-password" />
                </div>
                <Button type="submit" disabled={changingPassword} className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm" data-testid="change-password-btn">
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Biometric Sign-In (WebAuthn / Passkeys) */}
          <PasskeyManager userEmail={user?.email} />

          {/* Push Reminders (Web Push / VAPID) */}
          <Card className="border-[#36454F]/15 bg-white" data-testid="push-section">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F] flex items-center">
                <Bell className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                Push Reminders
              </CardTitle>
              <CardDescription className="font-['Manrope'] text-[#36454F]/50">
                Gentle nudges on your open disputes, family budget alerts, and a Sunday email digest.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <PushReminderToggle />
              <PushReminderPreferences />
            </CardContent>
          </Card>

          {/* Change Email */}
          <Card className="border-[#36454F]/15 bg-white" data-testid="email-section">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F] flex items-center">
                <Mail className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />Change Email
              </CardTitle>
              <CardDescription className="font-['Manrope'] text-[#36454F]/50">Current: {user?.email}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangeEmail} className="space-y-4">
                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">New Email</Label>
                  <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="newemail@example.com" className="mt-1 h-10 border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope']" required data-testid="new-email" />
                </div>
                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">Confirm with Password</Label>
                  <Input type="password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} className="mt-1 h-10 border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope']" required data-testid="email-password" />
                </div>
                <Button type="submit" disabled={changingEmail} className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm" data-testid="change-email-btn">
                  {changingEmail ? 'Updating...' : 'Update Email'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Notification Preferences */}
          <Card className="border-[#36454F]/15 bg-white" data-testid="notifications-section">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F] flex items-center">
                <Bell className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'welcome_email', label: 'Welcome email', desc: 'Receive a welcome message when you create your account' },
                { key: 'dispute_updates', label: 'Dispute updates', desc: 'Get notified when you mail a letter or receive a response' },
                { key: 'timer_reminders', label: '35-day reminders', desc: 'Reminder when your bureau response deadline approaches' },
                { key: 'resolve_celebrations', label: 'Resolution celebrations', desc: '"Your Slate is Clearing!" emails when disputes resolve' }
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-['Manrope'] font-medium text-[#36454F] text-sm">{label}</p>
                    <p className="text-xs text-[#36454F]/50 font-['Manrope']">{desc}</p>
                  </div>
                  <Switch
                    checked={notifPrefs[key]}
                    onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, [key]: checked }))}
                    data-testid={`toggle-${key}`}
                  />
                </div>
              ))}
              <Button onClick={handleSaveNotifications} disabled={savingNotifs} className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm" data-testid="save-notifications-btn">
                {savingNotifs ? 'Saving...' : 'Save Preferences'}
              </Button>
            </CardContent>
          </Card>

          {/* Privacy & Data */}
          <Card className="border-[#36454F]/15 bg-white" data-testid="privacy-section">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F] flex items-center">
                <Download className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />Privacy & Data
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-sm">
                <div>
                  <p className="font-['Manrope'] font-medium text-[#36454F] text-sm">Export my data</p>
                  <p className="text-xs text-[#36454F]/50 font-['Manrope']">Download all your data as a JSON file</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleExportData} className="border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white font-['Manrope'] rounded-sm" data-testid="export-data-btn">
                  <Download className="w-4 h-4 mr-1" strokeWidth={1.5} />Export
                </Button>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-sm">
                <div>
                  <p className="font-['Manrope'] font-medium text-[#36454F] text-sm">Delete uploaded reports</p>
                  <p className="text-xs text-[#36454F]/50 font-['Manrope']">Remove all credit report data and analyses</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleDeleteUploads} className="border-[#D9534F]/50 text-[#D9534F] hover:bg-[#D9534F] hover:text-white font-['Manrope'] rounded-sm" data-testid="delete-uploads-btn">
                  <Trash2 className="w-4 h-4 mr-1" strokeWidth={1.5} />Delete
                </Button>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-sm">
                <div>
                  <p className="font-['Manrope'] font-medium text-[#36454F] text-sm">Clear dispute history</p>
                  <p className="text-xs text-[#36454F]/50 font-['Manrope']">Delete all dispute letters and tracking data</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleDeleteDisputes} className="border-[#D9534F]/50 text-[#D9534F] hover:bg-[#D9534F] hover:text-white font-['Manrope'] rounded-sm" data-testid="delete-disputes-btn">
                  <Trash2 className="w-4 h-4 mr-1" strokeWidth={1.5} />Delete
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-[#D9534F]/30 bg-white" data-testid="danger-section">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-lg text-[#D9534F] flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" strokeWidth={1.5} />Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!showDeleteConfirm ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-['Manrope'] font-medium text-[#36454F] text-sm">Delete my account</p>
                    <p className="text-xs text-[#36454F]/50 font-['Manrope']">Permanently delete your account and all data. This cannot be undone.</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(true)} className="border-[#D9534F] text-[#D9534F] hover:bg-[#D9534F] hover:text-white font-['Manrope'] rounded-sm" data-testid="show-delete-btn">
                    Delete Account
                  </Button>
                </div>
              ) : (
                <div className="p-4 bg-[#D9534F]/5 border border-[#D9534F]/20 rounded-sm space-y-3">
                  <p className="text-sm text-[#D9534F] font-['Manrope'] font-medium">
                    Are you sure? This will permanently delete your account, all disputes, uploads, journal entries, and testimonies.
                  </p>
                  <div>
                    <Label className="text-sm font-['Manrope'] text-[#36454F]/80">Enter your password to confirm</Label>
                    <Input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} className="mt-1 h-10 border-[#D9534F]/30 focus:border-[#D9534F] rounded-sm font-['Manrope']" data-testid="delete-password" />
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={handleDeleteAccount} disabled={deleting} className="bg-[#D9534F] hover:bg-[#D9534F]/90 text-white font-['Manrope'] rounded-sm" data-testid="confirm-delete-btn">
                      {deleting ? 'Deleting...' : 'Permanently Delete'}
                    </Button>
                    <Button variant="ghost" onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); }} className="text-[#36454F]/50 font-['Manrope']">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
